import React from 'react';
import Alert from '@/components/UI/Alert';
import Badge from '@/components/UI/Badge';
import clsx from 'clsx';

interface ScoreResult {
    predictedProfit: number;
    baselineReturn: number;
    adjustmentFactor: number;
    adjustmentContribution?: number;
    matchedRules: number;
    confidence: number;
    decision: 'accept' | 'reject';
    sizingMultiplier?: number;
    entityNotInExperiment?: boolean;
    ruleEvaluations?: {
        ruleId: number;
        feature: {
            featureKey: string;
            serializedContextFeatureRef: string;
            label: string;
        };
        matched: boolean;
        lift?: number;
        contribution?: number;
    }[];
}

interface EntryScoreDisplayProps {
    scoreResult: ScoreResult | null;
    showDetails?: boolean;
    className?: string;
}

export const EntryScoreDisplay: React.FC<EntryScoreDisplayProps> = ({ scoreResult, showDetails = false, className }) => {
    const [expanded, setExpanded] = React.useState(false);

    if (!scoreResult) {
        return <div className={clsx('text-gray-500 text-sm', className)}>No scoring data available</div>;
    }

    const {
        predictedProfit,
        baselineReturn,
        adjustmentContribution,
        confidence,
        decision,
        matchedRules,
        sizingMultiplier,
        entityNotInExperiment
    } = scoreResult;

    // Handle entity not in experiment case
    if (entityNotInExperiment) {
        return (
            <Alert className={className} variant="warning">
                <div className="text-sm">
                    <p className="font-medium">Entity Not in Experiment</p>
                    <p className="text-xs mt-1">This entity was not included in the linked experiment. Entry rejected.</p>
                </div>
            </Alert>
        );
    }

    const isPositive = predictedProfit >= 0;
    const isProfitable = predictedProfit > 0;
    const isAccepted = decision === 'accept';
    const displayContribution = adjustmentContribution ?? predictedProfit - baselineReturn;

    return (
        <div className={clsx('space-y-3', className)}>
            {/* Main Display */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex flex-col">
                        <div className="flex items-baseline gap-2">
                            <span className={clsx('text-2xl font-bold', isProfitable ? 'text-green-600' : 'text-red-600')}>
                                {isPositive ? '+' : ''}
                                {predictedProfit.toFixed(1)}%
                            </span>
                            <span className="text-sm text-gray-500">predicted return</span>
                        </div>

                        <div className="flex items-center gap-2 mt-1">
                            <Badge variant={isAccepted ? 'success' : 'danger'}>{isAccepted ? 'Accept' : 'Reject'}</Badge>

                            {sizingMultiplier !== undefined && sizingMultiplier > 0 && (
                                <Badge variant="secondary">{sizingMultiplier.toFixed(1)}x size</Badge>
                            )}

                            <span className="text-xs text-gray-500">
                                {matchedRules} rule{matchedRules !== 1 ? 's' : ''} matched
                            </span>
                        </div>
                    </div>
                </div>

                {showDetails && (
                    <button
                        aria-expanded={expanded}
                        aria-label="Toggle details"
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        type="button"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? '▲' : '▼'}
                    </button>
                )}
            </div>

            {/* Confidence Bar */}
            <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-600">
                    <span>Confidence</span>
                    <span>{(confidence * 100).toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                        className={clsx(
                            'h-2 rounded-full transition-all',
                            confidence > 0.7 ? 'bg-green-500' : confidence > 0.4 ? 'bg-yellow-500' : 'bg-red-500'
                        )}
                        style={{ width: `${confidence * 100}%` }}
                    />
                </div>
            </div>

            {/* Expanded Details */}
            {showDetails && expanded && (
                <div className="pt-3 border-t space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Baseline:</span>
                            <span className="font-medium">{baselineReturn.toFixed(2)}%</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Contribution:</span>
                            <span className="font-medium">
                                {displayContribution >= 0 ? '+' : ''}
                                {displayContribution.toFixed(2)}pp
                            </span>
                        </div>
                    </div>

                    {/* Rule Evaluations */}
                    {scoreResult.ruleEvaluations && scoreResult.ruleEvaluations.length > 0 && (
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Matched Rules:</p>
                            <div className="space-y-1">
                                {scoreResult.ruleEvaluations
                                    .filter((rule) => rule.matched)
                                    .map((rule, idx) => (
                                        <div className="flex justify-between items-center text-xs px-2 py-1 bg-gray-50 rounded" key={idx}>
                                            <span className="text-gray-600 truncate max-w-[200px]">{rule.feature.label}</span>
                                            {rule.lift !== undefined && (
                                                <span className={clsx('font-medium', rule.lift > 0 ? 'text-green-600' : 'text-red-600')}>
                                                    {rule.lift > 0 ? '+' : ''}
                                                    {rule.lift.toFixed(1)}%
                                                </span>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Formula Breakdown */}
                    <div className="text-xs text-gray-500 p-2 bg-gray-50 rounded font-mono">
                        {baselineReturn.toFixed(2)}% {displayContribution >= 0 ? '+' : '-'} {Math.abs(displayContribution).toFixed(2)}pp ={' '}
                        {predictedProfit.toFixed(2)}%
                    </div>
                </div>
            )}
        </div>
    );
};
